import { apiClient } from './client';
import { MidInputSource, MidInspection, MidInspectionPayload } from '../types/midInspection';
import { SequentialCodeSuggestion } from '../types/sequentialCode';

export async function fetchMidInspections(params?: {
  inventoryId?: number;
  contractId?: number;
  keyword?: string;
}): Promise<MidInspection[]> {
  const { data } = await apiClient.get<MidInspection[]>('/mid-inspections', {
    params: {
      ...(params?.inventoryId != null ? { inventoryId: params.inventoryId } : {}),
      ...(params?.contractId != null ? { contractId: params.contractId } : {}),
      ...(params?.keyword?.trim() ? { q: params.keyword.trim() } : {}),
    },
  });
  return data;
}

export async function fetchMidAvailableInputs(keyword?: string): Promise<MidInputSource[]> {
  const { data } = await apiClient.get<MidInputSource[]>('/mid-inspections/available-inputs', {
    params: keyword?.trim() ? { q: keyword.trim() } : undefined,
  });
  return data;
}

export async function fetchNextMidLotNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/mid-inspections/suggestions/lot-no'
  );
  return data;
}

export async function fetchMidInspection(inspectionId: number): Promise<MidInspection> {
  const { data } = await apiClient.get<MidInspection>(`/mid-inspections/${inspectionId}`);
  return data;
}

export async function createMidInspection(payload: MidInspectionPayload): Promise<MidInspection> {
  const { data } = await apiClient.post<MidInspection>('/mid-inspections', payload);
  return data;
}

export async function updateMidInspection(
  inspectionId: number,
  payload: Partial<MidInspectionPayload>
): Promise<MidInspection> {
  const { data } = await apiClient.patch<MidInspection>(
    `/mid-inspections/${inspectionId}`,
    payload
  );
  return data;
}

export async function deleteMidInspection(inspectionId: number): Promise<void> {
  await apiClient.delete(`/mid-inspections/${inspectionId}`);
}

export async function approveMidInspection(inspectionId: number): Promise<MidInspection> {
  const { data } = await apiClient.post<MidInspection>(
    `/mid-inspections/${inspectionId}/approve`
  );
  return data;
}

export async function revokeMidInspection(inspectionId: number): Promise<MidInspection> {
  const { data } = await apiClient.post<MidInspection>(
    `/mid-inspections/${inspectionId}/revoke`
  );
  return data;
}

export async function downloadMidInspectionPdf(
  inspectionId: number,
  fileName?: string
): Promise<void> {
  try {
    const { data } = await apiClient.get<Blob>(`/mid-inspections/${inspectionId}/pdf`, {
      responseType: 'blob',
    });
    if (data.type && data.type.includes('application/json')) {
      const text = await data.text();
      const parsed = JSON.parse(text) as { message?: string };
      throw new Error(parsed.message || 'PDF 다운로드에 실패했습니다.');
    }
    const url = window.URL.createObjectURL(data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName || `중간검사성적서_${inspectionId}.pdf`;
    anchor.click();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    const blob = (err as { response?: { data?: Blob } })?.response?.data;
    if (blob instanceof Blob) {
      try {
        const text = await blob.text();
        const parsed = JSON.parse(text) as { message?: string };
        if (parsed.message) {
          throw new Error(parsed.message);
        }
      } catch (inner) {
        if (inner instanceof Error && inner.message && !inner.message.startsWith('Unexpected')) {
          throw inner;
        }
      }
    }
    if (err instanceof Error && err.message) {
      throw err;
    }
    throw err;
  }
}

export async function uploadMidInspectionAttachment(
  inspectionId: number,
  file: File
): Promise<MidInspection> {
  const body = new FormData();
  body.append('file', file);
  const { data } = await apiClient.post<MidInspection>(
    `/mid-inspections/${inspectionId}/attachments`,
    body,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteMidInspectionAttachment(
  inspectionId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/mid-inspections/${inspectionId}/attachments/${attachmentId}`);
}

export async function downloadMidInspectionAttachment(
  inspectionId: number,
  attachmentId: number
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/mid-inspections/${inspectionId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  return data;
}
